import {jsx, ParamComponent} from 'panel';

import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {ButtonEl} from 'common/components/button';
import {SvgIconEl} from 'common/components/svg-icon';
import {IconSizeType} from 'common/constants';
import {SelectEl} from 'common/components/select';
import {getEntityCreatorDisplayName} from 'common/report/stores/user-details';
import {userCookieUtils} from 'common/report/util/user-identity-from-cookie';
import {SelectTheme} from 'common/components/select/constants';
import {ButtonTheme} from 'common/components/button/constants';

import {MixpanelUserItem} from '../user-list/types';
import {UserListEl} from '../user-list';
import {UserIconEl} from '../user-icon';
import css from './index.inline.styl';

export type UserSelectorParams = {
  users?: MixpanelUserItem[];
  selectedUsers?: string[];
  icon?: string;
  label?: string;
  searchPlaceholder?: string;
  disableSelect?: boolean;
  hasSaveButton?: boolean;
  buttonTriggerText?: string;
  onSelectedUsersChange?: (d) => void;
  onMenuChange?: (d) => void;
  saveClicked?: () => void;
};
export type JsxProps = {
  params: UserSelectorParams;
};
// users that are selected after menu close/open
type UserSelectorState = {
  committedSelectedUsers?: string[];
};

const UsersSelectTrigger = ({cmp}: {cmp: UserSelector}) => {
  const selectedUsersList = cmp.getSelectedUsersList();

  let suffix = ``;
  if (selectedUsersList.length === 1) {
    suffix = selectedUsersList[0].name;
  }
  return (
    <div sel='.users-select-trigger' attrs={{slot: `trigger`}}>
      {cmp.params.buttonTriggerText ? (
        <ButtonEl
          attrs={{
            theme: ButtonTheme.Secondary,
            role: `button`,
          }}
        >
          {cmp.params.buttonTriggerText}
        </ButtonEl>
      ) : (
        <ButtonEl sel='.trigger-button'>
          <div sel='.trigger-label'>
            {cmp.params.icon ? <SvgIconEl sel='.trigger-icon' attrs={{icon: cmp.params.icon}} /> : null}
            {cmp.params.label ? <div sel='.trigger-label-text'>{cmp.params.label}</div> : null}
            {cmp.params.selectedUsers.length ? (
              <UserListEl
                sel='.trigger-users'
                attrs={{
                  users: selectedUsersList,
                  size: IconSizeType.Small,
                  'max-icons-displayed': 4,
                  elref: `userList`,
                }}
              />
            ) : null}
            {suffix && <div sel='.trigger-label-suffix'>{suffix}</div>}
            <SvgIconEl sel='.trigger-caret' attrs={{icon: `caret-down`}} />
          </div>
        </ButtonEl>
      )}
    </div>
  );
};

export class UserSelector extends ParamComponent<UserSelectorParams, UserSelectorState> {
  get config() {
    return {
      params: {
        users: Array,
        selectedUsers: Array,
        icon: String,
        label: String,
        searchPlaceholder: String,
        onSelectedUsersChange: Function,
        onMenuChange: Function,
        disableSelect: Boolean,
        hasSaveButton: Boolean,
        saveClicked: Function,
        buttonTriggerText: String,
      },
      defaultState: {
        committedSelectedUsers: [],
      },
      css,
      useShadowDom: true,
      template: () => {
        return (
          <div sel='.user-selector-container'>
            {this.params.disableSelect ? (
              <UserListEl
                sel='.trigger-users'
                attrs={{
                  users: this.getSelectedUsersList(),
                  size: IconSizeType.Small,
                  'max-icons-displayed': 4,
                  elref: `userList`,
                }}
              />
            ) : (
              <SelectEl
                sel='.user-selector'
                attrs={{
                  multiple: true,
                  'multiple-save-button': this.params.hasSaveButton,
                  'search-placeholder': this.params.searchPlaceholder,
                  elref: `creatorSelect`,
                  theme: SelectTheme.Secondary,
                  ...(this.params.buttonTriggerText ? {'menu-width': 300} : {}),
                }}
                props={{sections: this.getUserSelectorSections()}}
                on={{
                  change: (ev) => this.params.onSelectedUsersChange(ev.detail.item),
                  menuChange: (ev) => {
                    this.update({committedSelectedUsers: this.params.selectedUsers});
                    this.params.onMenuChange?.(ev.detail.isOpen);
                  },
                  saveClicked: this.params.saveClicked,
                }}
              >
                <UsersSelectTrigger cmp={this} />
              </SelectEl>
            )}
          </div>
        );
      },
    };
  }

  getSelectedUsersList() {
    return this.params.users.filter((user) => this.params.selectedUsers.includes(user.email));
  }
  getUserSelectorSections() {
    // pushing commited selected users to top
    let items = this.params.users
      .filter((user) => this.state.committedSelectedUsers.includes(user.email))
      .map((user) => ({
        isSelected: this.params.selectedUsers.includes(user.email),
        label: this.getUserNameLabel(user),
        value: user.email,
        user,
      }));
    items = [
      ...items,
      ...this.params.users
        .filter((user) => !this.state.committedSelectedUsers.includes(user.email))
        .map((user) => ({
          isSelected: this.params.selectedUsers.includes(user.email),
          label: this.getUserNameLabel(user),
          value: user.email,
          user,
        })),
    ];

    return [
      {
        items,
        iconRenderer: (item) => {
          return (
            <UserIconEl
              style={{'margin-right': `6px`}}
              attrs={{
                id: item.user.id,
                email: item.user.email,
                name: item.user.name,
                size: IconSizeType.Small,
                'disable-tooltip': true,
                'disable-sidebar': true,
              }}
            />
          );
        },
      },
    ];
  }

  connectedCallback() {
    super.connectedCallback();
  }

  getUserNameLabel(user) {
    const name = getEntityCreatorDisplayName(user.email, user.name);
    return name + (userCookieUtils.getUserIdentityFromCookie()?.id === user.id ? ` (you)` : ``);
  }
}

export const UserSelectorEl = defineAndMakeJsxFactory<JsxProps>(`mp-user-selector`, UserSelector);
export const UserSelectorReact = panel2react<JsxProps>(`mp-user-selector`, UserSelector);
